import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { CameraSnapshotUrlResponseDto } from "../../../models/court-recording/dto/venue-agent.dto";

/** Which camera: its MAC when the box has resolved it, else the court it records. */
export type CameraSnapshotSubject = { mac: string } | { courtId: number };

export const GET_CAMERA_SNAPSHOT_URL_QUERY_KEY = (
    agentId: string,
    subject: CameraSnapshotSubject,
    lastSnapshotAt: string | undefined,
) => [
    'court-recordings',
    'venue-agents',
    agentId,
    'snapshot-url',
    'mac' in subject ? `mac:${subject.mac}` : `court:${subject.courtId}`,
    lastSnapshotAt ?? 'none',
];

/**
 * A short-lived read URL for one camera's latest picture.
 *
 * Opaque-URL contract, same as `useGetCourtRecordingVideoUrl`: the URL is
 * rendered straight into an `<img>` and never parsed or stored. Keyed on the
 * camera's `lastSnapshotAt` so a fresh capture (which overwrites the same
 * blob) fetches a NEW signed URL and the image reloads — the signature makes
 * the URL distinct, which is what defeats the browser cache on an otherwise
 * identical path. `enabled` only once a picture exists: before the first
 * capture there is no blob to read, and a failed GET would only paint a
 * broken image where the placeholder belongs.
 */
export const useGetCameraSnapshotUrl = (
    agentId: string,
    organizationId: string,
    subject: CameraSnapshotSubject | null,
    lastSnapshotAt: string | undefined,
) => {
    const client = useApiClient();
    const query = subject
        ? 'mac' in subject
            ? `mac=${encodeURIComponent(subject.mac)}`
            : `courtId=${subject.courtId}`
        : '';

    return useQuery({
        queryKey: GET_CAMERA_SNAPSHOT_URL_QUERY_KEY(
            agentId,
            subject ?? { courtId: 0 },
            lastSnapshotAt,
        ),
        queryFn: () =>
            client.fetchApi<CameraSnapshotUrlResponseDto, {}>({
                endpoint: `court-recordings/admin/venue-agents/${agentId}/snapshot-url?organizationId=${organizationId}&${query}`,
                method: 'GET',
            }),
        enabled: Boolean(agentId && organizationId && subject && lastSnapshotAt),
        // The API signs for 5 minutes; refetch a little before that.
        staleTime: 4 * 60 * 1000,
        retry: false,
    });
};
